import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  ResourceManagerService,
  ResourceSet,
} from '@barthauer/abc/core/resources';
import { ActionType } from '../../map.component';
import { MapLoader } from '../../map.loader';
import { MapView } from '../../map.view';
declare var ol: any;

@Component({
    selector: 'abc-graphics-measure',
    templateUrl: './measure.component.html',
    styleUrls: ['./measure.component.scss'],
    standalone: false
})
export class MeasureComponent implements AfterViewInit, OnInit {
  @ViewChild('toolTip', { static: false }) toolTip: ElementRef;
  @ViewChild('measureToolTipCoord', { static: false })
  measureToolTipCoord: ElementRef;
  @ViewChild('measureToolTip', { static: false }) measureToolTip: ElementRef;
  @ViewChild('measureToolTipArea', { static: false })
  measureToolTipArea: ElementRef;
  @ViewChild('viewContainer', { read: ViewContainerRef })
  viewContainer: ViewContainerRef;
  @ViewChild('measureToolTipStaticTemplate', { static: false })
  measureStaticTemplate: TemplateRef<any>;
  @ViewChild('measureToolTipStaticAreaTemplate', { static: false })
  measureStaticAreaTemplate: TemplateRef<any>;

  toolTipText: string;
  measureTextCoord: string;
  measureText: string;
  measureTextArea: string;
  measureTextStatic: string;

  private mapView: MapView;
  private map: any;
  private draw: any;
  private snap: any;
  private vectorEditlayer: any;
  private vectorSource: any;
  private sketch: any;
  private listener: any;

  private tooltipCoord: any;
  private measureTooltipOverlay: any;
  private actionType: ActionType;

  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;

  constructor(
    private cd: ChangeDetectorRef,
    private resourceService: ResourceManagerService
  ) {
    this.resAsync = this.resourceService.getResourceSet(
      'abc-graphics-adapters-open-layers'
    );
  }

  async ngOnInit() {
    this.res = await this.resAsync;
  }

  ngAfterViewInit(): void {}

  ensureInteraction(mapView: MapView, actionType: ActionType) {
    this.mapView = mapView;
    this.map = mapView.getMap();
    this.actionType = actionType;
    this.clearInteraction();
    this.ensureMeasureEnvironment();
  }

  ensureMeasureEnvironment() {
    this.ensureEditSource();
    let drawType: string = undefined;
    switch (this.actionType) {
      case ActionType.MeasureLine:
        drawType = 'LineString';
        break;
      case ActionType.MeasureArea:
        drawType = 'Polygon';
        break;
      case ActionType.MeasureCoord:
        drawType = 'Point';
        break;
    }

    if (drawType) {
      this.draw = new ol.interaction.Draw({
        source: this.vectorSource,
        type: drawType,
        style: this.getDrawStyle(),
      });

      this.map.addInteraction(this.draw);
      this.snap = new ol.interaction.Snap({ source: this.vectorSource });
      this.map.addInteraction(this.snap);
      this.map.getViewport().addEventListener('mouseout', this.onMouseout);

      this.draw.on('drawstart', (event) => this.onDrawStart(event));
      this.draw.on('drawend', (event) => this.onDrawEnd(event));
    }

    this.createTooltipOverlay();
  }

  private createTooltipOverlay() {
    this.measureTooltipOverlay = new ol.Overlay({
      element: this.measureToolTip.nativeElement,
      offset: [0, -15],
      positioning: 'bottom-center',
    });

    this.map.addOverlay(this.measureTooltipOverlay);
  }

  onMouseout() {
    this.toolTip?.nativeElement?.classList.add('hidden');
  }

  onDrawEnd(evt: any) {
    this.measureTooltipOverlay?.setPosition(undefined);
    this.sketch = undefined;
    ol.Observable.unByKey(this.listener);
  }

  onDrawStart(evt: any) {
    this.sketch = evt.feature;
    this.listener = this.sketch
      .getGeometry()
      .on('change', (evt) => this.onSketchGeometryChange(evt));
  }

  onSketchGeometryChange(evt: any) {
    var geom = this.mapView.transformGeometryToDataProjection(evt.target);
    var output = '';
    if (geom instanceof ol.geom.Polygon) {
      output = this.formatArea(geom);
      this.measureTextArea = output;
      this.measureTooltipOverlay.setElement(
        this.measureToolTipArea.nativeElement
      );
      this.tooltipCoord = evt.target.getLastCoordinate();
    } else if (geom instanceof ol.geom.LineString) {
      output = this.formatLength(geom);
      this.measureText = output;
      this.measureTooltipOverlay.setElement(this.measureToolTip.nativeElement);
      this.tooltipCoord = evt.target.getLastCoordinate();
    }

    this.measureTooltipOverlay.setPosition(this.tooltipCoord);
    this.cd.detectChanges();
  }

  formatCoordinate(point: any): string {
    let output = '';
    if (point) {
      let coord = point.getCoordinates();
      let curProj = this.getCurrentProjection();
      const latlongCoord = ol.proj.toLonLat(coord, curProj);
      const latLon: [number, number] = [latlongCoord[1], latlongCoord[0]];
      const template = '{x}, {y}';
      const xy = ol.coordinate.format(coord, template, 3);
      const ll = ol.coordinate.format(latLon, template, 9);
      if (latlongCoord[0] == coord[0] && latlongCoord[1] == coord[1]) {
        output = ll;
      } else {
        output = xy + '\n' + ll;
      }
      this.copyMessage(output);
    }

    return output;
  }

  private getCurrentProjection(): any {
    let curProj = this.map.getView().getProjection();
    if (this.mapView.dataProjection && this.mapView.featureProjection) {
      curProj = this.mapView.dataProjection;
    }
    return curProj;
  }

  copyMessage(val: string) {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.height = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  private formatLength(line: any): string {
    var length = line.getLength();
    var proj = this.getCurrentProjection();
    let factor = ol.proj.Units.METERS_PER_UNIT[proj.getUnits()];
    if (factor) {
      length *= factor;
    }
    var output;
    if (length >= 2000) {
      output = Math.round((length / 1000) * 1000) / 1000 + ' ' + 'km';
    } else {
      output = Math.round(length * 100) / 100 + ' ' + 'm';
    }
    return output;
  }

  private formatArea(polygon: any) {
    var area = polygon.getArea();
    var proj = this.getCurrentProjection();
    let factor = ol.proj.Units.METERS_PER_UNIT[proj.getUnits()];
    if (factor) {
      area *= factor * factor;
    }
    var output;
    if (area >= 20000) {
      output = Math.round((area / 1000000) * 1000) / 1000 + ' ' + 'km²';
    } else {
      output = Math.round(area * 100) / 100 + ' ' + 'm²';
    }
    return output;
  }

  private getDrawStyle() {
    return new ol.style.Style({
      fill: new ol.style.Fill({
        color: 'rgba(255, 255, 255, 0.2)',
      }),
      stroke: new ol.style.Stroke({
        color: 'rgba(0, 0, 0, 0.5)',
        lineDash: [10, 10],
        width: 2,
      }),
      image: this.getCrossStyle(),
    });
  }

  getCrossStyle() {
    return new ol.style.RegularShape({
      fill: new ol.style.Fill({
        color: 'red',
      }),
      points: 4,
      radius1: 15,
      radius2: 1,
    });
  }

  ensureEditSource() {
    this.vectorSource = new ol.source.Vector();
    this.vectorEditlayer = this.createVectorLayer(this.vectorSource);
    this.map.addLayer(this.vectorEditlayer);
  }

  createVectorLayer(src: any): any {
    let vector = new ol.layer.Vector({
      source: src,
      style: this.getMeasureStyle.bind(this),
      zIndex: Infinity,
    });

    return vector;
  }

  getMeasureStyle(feature, resolution) {
    return new ol.style.Style({
      fill: new ol.style.Fill({
        color: 'rgba(255, 255, 255, 0.2)',
      }),
      stroke: new ol.style.Stroke({
        color: '#ffcc33',
        width: 2,
      }),
      image: this.getLocationIconStyle(),
      text: this.getLabelTextStyle(feature, resolution),
      zIndex: Infinity,
    });
  }

  getLocationIconStyle() {
    return new ol.style.Icon({
      anchor: [0.5, 0.8],
      opacity: 1,
      scale: 1,
      crossOrigin: 'Anonymous',
      src: this.res.svgUrl('Map-MeasureCoordinates-Pointer'),
    });
  }

  getLabelTextStyle(feature, resolution) {
    let isPointFeature: boolean = false;
    let hasSharpTurn: boolean = false;
    let extent: number[];
    if (feature) {
      const geom = feature.getGeometry();
      extent = geom?.getExtent();
      isPointFeature = geom && geom instanceof ol.geom.Point;
      if (geom && geom instanceof ol.geom.LineString) {
        hasSharpTurn = this.hasSharpTurn(geom, 0.5);
      }
    }

    let sizeInMeters: number = 2;
    if (extent?.length === 4) {
      const width = extent[2] - extent[0];
      const height = extent[3] - extent[1];
      const size = Math.max(Math.abs(width), Math.abs(height));
      if (size > 10) {
        sizeInMeters *= Math.log10(size);
      }
    }
    const fontSize = Math.round(
      sizeInMeters * MapLoader.getSizeFactor(resolution)
    );

    return new ol.style.Text({
      placement: this.getPlacementByGeometry(feature, hasSharpTurn),
      text: this.getTextFromFeature(feature, resolution),

      font: `bold ${fontSize}px sans-serif`,
      textBaseline: 'bottom',
      fill: new ol.style.Fill({ color: 'white' }),
      stroke: isPointFeature
        ? undefined
        : new ol.style.Stroke({ color: '#0A96FA', width: 3 }),
      offsetX: this.getLabelOffsetByGeometry(feature),
      offsetY: this.getLabelOffsetByGeometry(feature, false),
      textAlign: isPointFeature ? 'left' : 'center',
      backgroundFill: isPointFeature
        ? new ol.style.Fill({ color: '#0A96FA' })
        : undefined,
      backgroundStroke: isPointFeature
        ? new ol.style.Stroke({ color: '#193755', width: 1 })
        : undefined,
      padding: isPointFeature ? [5, 5, 5, 5] : [0, 0, 0, 0],
      overflow: true,
    });
  }

  hasSharpTurn(
    lineString: any,
    fraction: number = 0.5,
    sharpTurnThreshold: number = Math.PI / 6
  ): boolean {
    let retHas: boolean = false;
    const midpoint = lineString.getCoordinateAt(fraction);
    const coordinates = lineString.getCoordinates();

    if (coordinates.length <= 2) {
      return retHas;
    }

    for (let i = 1; i < coordinates.length - 1; i++) {
      const prevPoint = coordinates[i - 1];
      const curPoint = coordinates[i];
      const nextPoint = coordinates[i + 1];
      const vector1 = [curPoint[0] - prevPoint[0], curPoint[1] - prevPoint[1]];
      const vector2 = [nextPoint[0] - curPoint[0], nextPoint[1] - curPoint[1]];
      const dotProduct = vector1[0] * vector2[0] + vector1[1] * vector2[1];
      const magnitude1 = Math.sqrt(vector1[0] ** 2 + vector1[1] ** 2);
      const magnitude2 = Math.sqrt(vector2[0] ** 2 + vector2[1] ** 2);

      const angle = Math.acos(dotProduct / (magnitude1 * magnitude2));

      retHas = angle > sharpTurnThreshold;
      if (retHas) {
        break;
      }
    }

    return retHas;
  }

  getLabelOffsetByGeometry(feature: any, isOffsetX = true) {
    let ret = 0;

    if (feature) {
      const geom = feature.getGeometry();
      if (geom) {
        if (geom instanceof ol.geom.Point) {
          if (isOffsetX) ret = 15;
          else ret = -15;
        }
      }
    }

    return ret;
  }

  getPlacementByGeometry(feature: any, hasSharpTurn: boolean = false) {
    let ret: string = 'point';
    if (feature) {
      const geom = feature.getGeometry();
      if (geom) {
        if (geom instanceof ol.geom.LineString) {
          if (!hasSharpTurn) {
            ret = 'line';
          }
        }
      }
    }

    return ret;
  }

  getTextFromFeature(feature: any, resolution: any) {
    let ret: string = '';
    if (feature) {
      let geom = this.mapView.transformGeometryToDataProjection(
        feature.getGeometry()
      );
      if (geom) {
        let output: string = '';
        if (geom instanceof ol.geom.Polygon) {
          output = this.formatArea(geom);
        } else if (geom instanceof ol.geom.LineString) {
          output = this.formatLength(geom);
        } else if (geom instanceof ol.geom.Point) {
          output = this.formatCoordinate(geom);
        }

        ret = output;
      }
    }
    return ret;
  }

  clearInteraction(removeMeasureElements: boolean = true) {
    if (this.map) {
      this.map.removeInteraction(this.draw);
      this.map.removeInteraction(this.snap);

      if (removeMeasureElements) {
        this.map.removeLayer(this.vectorEditlayer);
        this.vectorEditlayer = undefined;
        this.vectorSource = undefined;
        this.measureTooltipOverlay?.setPosition(undefined);
        if (this.viewContainer) {
          this.viewContainer.clear();
        }
      }
    }
  }
}
